import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { apiBaseUrl } from '../api/client'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Logomark } from '../components/Logomark'

// Unsubscribe page of the daily e-mail digest (spec 2026-10-27-23).
//
// It lives outside routes/$org, so it inherits no auth guard, like /wo: the
// reader followed a link from their own digest, maybe on a phone where they
// are not signed in. The `email_digest` token in the path is the whole
// authorization, and it can only turn the digest off. The one-click RFC 8058
// POST of mail clients hits the same endpoint without this page.
export const Route = createFileRoute('/u/$orgCode/$token/unsubscribe')({
    component: DigestUnsubscribePage,
})

type State = 'idle' | 'sending' | 'done' | 'not_found' | 'error'

function DigestUnsubscribePage() {
    const { orgCode, token } = Route.useParams()
    const { t } = useTranslation()
    const [state, setState] = useState<State>('idle')

    const unsubscribe = async () => {
        setState('sending')
        try {
            const res = await fetch(
                `${apiBaseUrl}/u/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/digest-unsubscribe`,
                { method: 'POST' },
            )
            setState(res.ok ? 'done' : res.status === 404 ? 'not_found' : 'error')
        } catch {
            setState('error')
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md" data-testid="digest-unsubscribe">
                <CardContent className="space-y-4 pt-6 text-center">
                    <Logomark className="mx-auto h-8" />
                    <p className="text-lg font-semibold">{t('digest_unsubscribe.title')}</p>
                    {state === 'done' ? (
                        <p className="text-sm" data-testid="digest-unsubscribe-done">{t('digest_unsubscribe.done')}</p>
                    ) : state === 'not_found' ? (
                        <p className="text-sm text-destructive" data-testid="digest-unsubscribe-not-found">
                            {t('digest_unsubscribe.not_found')}
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground">{t('digest_unsubscribe.body')}</p>
                            <Button onClick={() => void unsubscribe()} disabled={state === 'sending'} data-testid="digest-unsubscribe-button">
                                {t('digest_unsubscribe.button')}
                            </Button>
                            {state === 'error' && (
                                <p className="text-sm text-destructive" data-testid="digest-unsubscribe-error">{t('digest_unsubscribe.error')}</p>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
